/**
 * PDF Lab — OpenDataLoader PDF, every mode it has.
 *
 * WHAT THIS TOOL IS FOR, STATED ONCE. OpenDataLoader is a Java structure
 * parser: it reads a PDF's content stream and rebuilds reading order, headings
 * and tables from coordinates that a born-digital PDF already carries. It is
 * not an OCR model, and on a scanned page its native mode has nothing to read.
 * Hybrid mode hands pages to docling — a different project, whose OCR engine is
 * EasyOCR by default — and structures the result. Both halves are exposed here
 * because a bench that blurs them would produce a number nobody can act on.
 *
 * Layout mirrors OCR Lab's three bands so the two tools read as one product:
 *   1. Source + Run      — the document and the button
 *   2. Options           — everything the engine supports, from its own catalogue
 *   3. Result            — structure over the page, then the raw artefacts
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlay, FiSave, FiUpload } from "react-icons/fi";
import { Button, Card, Input, PillBadge, Spinner } from "@/components/ui";
import { BADGE_TONE } from "@/components/ui/pillTones";
import { HybridPanel, type HybridSettings } from "@/components/pdflab/HybridPanel";
import { OptionsPanel, summariseOptions } from "@/components/pdflab/OptionsPanel";
import { OutputTabs } from "@/components/pdflab/OutputTabs";
import { StructureView } from "@/components/pdflab/StructureView";
import { api } from "@/lib/api";
import { renderPdfPage, type RenderedPage } from "@/lib/pdf";
import { flattenNodes, type ConvertResult, type EnginePart, type OptionCatalogue } from "@/lib/pdflab";

/**
 * Starting options. JSON and Markdown, because those are the two artefacts the
 * rest of the page is built on: the JSON tree drives the structure overlay, and
 * markdown is what a RAG pipeline would actually consume.
 */
const DEFAULT_VALUES: Record<string, any> = { format: ["json", "markdown"] };

export default function PdfLab() {
  const [catalogue, setCatalogue] = useState<OptionCatalogue | null>(null);
  const [values, setValues] = useState<Record<string, any>>(DEFAULT_VALUES);
  const [hybridSettings, setHybridSettings] = useState<HybridSettings>({});
  const [parts, setParts] = useState<EnginePart[]>([]);
  const [hybridBusy, setHybridBusy] = useState(false);

  const [pdf, setPdf] = useState<ArrayBuffer | null>(null);
  const [page, setPage] = useState<RenderedPage | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [uploadKey, setUploadKey] = useState("");
  const [filename, setFilename] = useState("");

  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [saveLabel, setSaveLabel] = useState("");
  const [saved, setSaved] = useState(false);

  const usesHybrid = Boolean(values.hybrid && values.hybrid !== "off");

  useEffect(() => {
    api
      .get("/api/pdflab/options")
      .then(setCatalogue)
      .catch((e) => setErr(`Could not read the engine's options: ${e.message}`));
  }, []);

  // Poll only while something is mid-transition. A backend that is off or ready
  // is not going to change on its own, and a permanent 5-second poll is a cost
  // with no reader.
  const transitioning = useMemo(
    () => parts.some((p) => p.phase === "starting"),
    [parts],
  );
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const d = await api.get("/api/pdflab/status");
        if (alive) setParts(d.parts);
      } catch { /* the status strip degrades; runs still work */ }
    };
    tick();
    const t = setInterval(tick, transitioning ? 5000 : 30000);
    return () => { alive = false; clearInterval(t); };
  }, [transitioning]);

  // Reopening a saved run: /pdflab?run=<id> restores its settings and artefacts.
  useEffect(() => {
    const runId = new URLSearchParams(window.location.search).get("run");
    if (!runId) return;
    (async () => {
      try {
        const run = await api.get(`/api/pdflab/runs/${runId}`);
        setValues(run.options ?? DEFAULT_VALUES);
        setHybridSettings(run.hybridSettings ?? {});
        setFilename(run.filename ?? "");
        setSaved(true);
        if (run.resultKey) setResult(await fetchResult(run.resultKey));
      } catch {
        setErr("Could not load that saved run. Artefacts expire after 30 days.");
      }
    })();
  }, []);

  const onFile = useCallback(async (file: File) => {
    setErr(""); setResult(null); setSaved(false);
    setFilename(file.name);
    const buf = await file.arrayBuffer();
    setPdf(buf);
    const { page: p, count } = await renderPdfPage(buf, 1);
    setPage(p); setPageCount(count); setPageNum(1);
    try {
      const { url, key } = await api.post("/api/pdflab/uploads", {
        filename: file.name,
        contentType: file.type || "application/pdf",
      });
      await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      setUploadKey(key);
    } catch (e: any) {
      // Unlike the OCR bench, this is FATAL rather than cosmetic: the parser
      // reads the document from S3, so with no upload there is nothing to run.
      setErr(`Upload failed, so there is nothing to parse: ${e.message}`);
    }
  }, []);

  const gotoPage = useCallback(async (n: number) => {
    if (!pdf || n < 1 || n > pageCount) return;
    const { page: p } = await renderPdfPage(pdf, n);
    setPage(p);
    setPageNum(n);
  }, [pdf, pageCount]);

  const run = useCallback(async () => {
    if (!uploadKey) return;
    setRunning(true); setErr(""); setNote(""); setResult(null); setSaved(false); setElapsed(0);
    const started = Date.now();
    try {
      const { jobId } = await api.post("/api/pdflab/convert", {
        uploadKey, filename, options: values, hybridSettings,
      });

      // Poll. Standard mode is ~1s, but the same button in hybrid mode is
      // minutes — and the first hybrid run also builds a converter before it
      // starts, which is why the note below exists rather than a silent wait.
      for (;;) {
        await new Promise((r) => setTimeout(r, 1500));
        const job = await api.get(`/api/pdflab/convert/${jobId}`);
        setElapsed(Math.round((Date.now() - started) / 1000));
        if (usesHybrid && Date.now() - started > 20000) {
          setNote("Hybrid mode: the first run at a given set of OCR settings loads docling's models before it can start. Later runs at the same settings skip that.");
        }
        if (job.status === "done" || job.status === "failed") {
          if (job.results?.resultKey) {
            setResult(await fetchResult(job.results.resultKey));
            (window as any).__lastRun = { jobId, resultKey: job.results.resultKey };
          }
          if (job.status === "failed") setErr(job.error ?? "The run failed.");
          break;
        }
      }
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setRunning(false);
      setNote("");
    }
  }, [uploadKey, filename, values, hybridSettings, usesHybrid]);

  const save = useCallback(async () => {
    const last = (window as any).__lastRun;
    if (!last?.resultKey) return;
    await api.post("/api/pdflab/runs", {
      runId: last.jobId,
      label: saveLabel,
      filename,
      uploadKey,
      resultKey: last.resultKey,
      options: values,
      hybridSettings,
      hybrid: usesHybrid,
      formats: values.format ?? [],
      ms: result?.ms,
      pageCount,
    });
    setSaved(true);
  }, [saveLabel, filename, uploadKey, values, hybridSettings, usesHybrid, result, pageCount]);

  const setHybridReplicas = async (action: "start" | "stop") => {
    setHybridBusy(true);
    try {
      const d = await api.post(`/api/pdflab/hybrid/${action}`, {});
      setParts(d.parts);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setHybridBusy(false);
    }
  };

  const nodes = useMemo(
    () => (result?.outputs?.json ? flattenNodes(result.outputs.json) : []),
    [result],
  );
  const core = parts.find((p) => p.id === "core");

  return (
    <div className="mx-auto max-w-7xl space-y-4 px-4 py-6 md:px-6">
      {/* ---- 1. source + run ---- */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <label className="fh-focus inline-flex cursor-pointer items-center gap-2 rounded-fh-button bg-white px-4 py-2 text-fh-row text-fh-text-strong shadow-[inset_0_0_0_1px_var(--color-fh-border-button)] transition hover:bg-fh-surface-soft">
              <FiUpload />
              {filename ? "Choose another PDF" : "Choose a PDF"}
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
              />
            </label>
            {filename && (
              <span className="truncate text-fh-body text-fh-text-secondary">
                {filename}
                {pageCount ? ` · ${pageCount} page${pageCount === 1 ? "" : "s"}` : ""}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {core && core.phase !== "ready" && (
              <PillBadge
                value={`parser ${core.phase}`}
                tone={core.phase === "error" ? BADGE_TONE.red : BADGE_TONE.amber}
              />
            )}
            {running && (
              <span className="flex items-center gap-2 text-fh-meta text-fh-text-muted">
                <Spinner className="h-4 w-4" /> {elapsed}s
              </span>
            )}
            <Button onClick={run} disabled={!uploadKey || running}>
              <FiPlay className="mr-1.5" /> Run
            </Button>
          </div>
        </div>

        <p className="mt-2 text-fh-meta leading-relaxed text-fh-text-muted">
          {summariseOptions(values)}
        </p>

        {note && (
          <p className="mt-2 rounded-fh-input bg-fh-badge-blue-bg px-3 py-2 text-fh-meta leading-relaxed text-fh-badge-blue-text">
            {note}
          </p>
        )}
        {err && (
          <p className="mt-2 rounded-fh-input bg-fh-badge-red-bg px-3 py-2 text-fh-meta leading-relaxed text-fh-badge-red-text">
            {err}
          </p>
        )}
      </Card>

      {/* ---- 2. options ---- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          {catalogue ? (
            <OptionsPanel
              options={catalogue.options}
              values={values}
              reserved={catalogue.reserved}
              onChange={setValues}
            />
          ) : (
            <Card className="p-6">
              <Spinner />
            </Card>
          )}
        </div>
        <div className="space-y-3">
          <HybridPanel
            parts={parts}
            settings={hybridSettings}
            enabled={usesHybrid}
            busy={hybridBusy}
            onSettings={setHybridSettings}
            onStart={() => setHybridReplicas("start")}
            onStop={() => setHybridReplicas("stop")}
          />
          {catalogue && (
            <p className="px-1 text-fh-meta leading-relaxed text-fh-text-muted">
              Engine version {catalogue.version}. Every control above is generated from the flags
              this deployed version reports, so it cannot offer one the parser does not have.
            </p>
          )}
        </div>
      </div>

      {/* ---- 3. result ---- */}
      {result && (
        <div className="space-y-4">
          <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <PillBadge
                value={result.ok ? "ok" : "failed"}
                tone={result.ok ? BADGE_TONE.green : BADGE_TONE.red}
              />
              <span className="text-fh-body text-fh-text-secondary">
                {result.ms}ms · {Object.keys(result.outputs ?? {}).length} artefact
                {Object.keys(result.outputs ?? {}).length === 1 ? "" : "s"}
                {nodes.length ? ` · ${nodes.length} nodes` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                placeholder="Label this run"
                className="w-56"
              />
              <Button variant="secondary" onClick={save} disabled={saved}>
                <FiSave className="mr-1.5" /> {saved ? "Saved" : "Save"}
              </Button>
            </div>
          </Card>

          {result.outputs?.json ? (
            <StructureView
              nodes={nodes}
              page={page}
              pageNumber={pageNum}
              pageCount={pageCount}
              onPageChange={gotoPage}
            />
          ) : (
            <Card className="p-4 text-fh-meta leading-relaxed text-fh-text-muted">
              The structure view needs the <span className="font-medium">json</span> format — it is
              the only artefact that carries bounding boxes. Tick it in Output and run again.
            </Card>
          )}

          <OutputTabs result={result} />
        </div>
      )}
    </div>
  );
}

/**
 * Fetch a result payload.
 *
 * Two hops on purpose: the API hands out a presigned URL and the browser reads
 * the object straight from S3. A run with every format enabled is several MB,
 * which is both wasteful to proxy through the Lambda and close enough to API
 * Gateway's response ceiling to be a latent failure rather than a design.
 */
async function fetchResult(resultKey: string): Promise<ConvertResult> {
  const { url } = await api.get(`/api/pdflab/result?key=${encodeURIComponent(resultKey)}`);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`could not read the result (${res.status})`);
  return res.json();
}
